import type { RiskGapModel } from '@ctw/shared/api/fhir/models/risk-opportunity';
import { usePatientRisks } from '@ctw/shared/api/risk-service';
import { ResourceOverviewCardGrouped } from '@ctw/shared/content/overview/resource-overview-card-grouped';
import { withOverviewCardErrorBoundary } from '@ctw/shared/content/overview/with-overview-card-error-boundary';
import { useFilteredSortedData } from '@ctw/shared/hooks/use-filtered-sorted-data';
import { useRiskGapDrawer } from '@ctw/shared/hooks/use-risk-opportunity-drawer';
import { tw } from '@ctw/shared/utils/tailwind';
import { faCalendar } from '@fortawesome/pro-regular-svg-icons';
import { faStarExclamation } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import type { OverviewFilter } from '../overview/filters';
import type { SpecificResourceOverviewCardProps } from '../overview/resource-overview-card';
import { ResourceTitleColumn } from '../resource/resource-title-column';
import { ConfidenceLabel } from './helpers/confidence-signals';
import { defaultRiskSort } from './helpers/sorts';
import { RiskGapView } from './helpers/views';

export const filters = (): Array<OverviewFilter<RiskGapModel>> => [
  {
    key: 'risk-opportunities',
    label: 'risk opportunities',
    description: 'risk opportunities',
    predicate: (r: RiskGapModel) => r.type === 'recapture' || r.type === 'suspect',
  },
];

export interface PatientRiskGapOverviewProps extends SpecificResourceOverviewCardProps {
  onAfterDetailsDrawerClosed?: () => void;
  RiskGapIdForDetailsDrawer?: string;
  onOpenDetailsDrawer?: (RiskGap: RiskGapModel) => void;
}

export const PatientRiskGapOverview = withOverviewCardErrorBoundary({
  cardTitle: 'Risk Capture',
  boundaryName: 'PatientRiskGapOverview',
  Component: ({ onSeeAllResources, onOpenDetailsDrawer }: PatientRiskGapOverviewProps) => {
    const { openDrawer } = useRiskGapDrawer();
    const { data = [], isLoading } = usePatientRisks();

    const { data: filteredSortedData } = useFilteredSortedData({
      defaultSort: defaultRiskSort,
      viewOptions: [RiskGapView.all],
      records: data,
      defaultView: RiskGapView.all.display,
    });

    return (
      <ResourceOverviewCardGrouped
        groupBy={(record) => String(record.type)}
        onRowClick={({ telemetry, record }) => {
          onOpenDetailsDrawer?.(record);
          openDrawer({ telemetry, model: record as never });
        }}
        data={filteredSortedData}
        emptyStateMessage={
          data.length === 0
            ? "We didn't find any risk capture or suspect opportunities for this patient."
            : 'There are no risk capture or suspect opportunities for this patient.'
        }
        footerCTA={
          data.length === 0
            ? undefined
            : {
                label: 'All Risk Capture',
                onClick: onSeeAllResources,
              }
        }
        title="Risk Capture"
        helpText="Risk suspecting and recapture opportunities"
        loading={isLoading}
        headerIcon={faStarExclamation}
        testId="risk-opportunities-overview"
        renderResource={(record: RiskGapModel) => (
          <div className={tw`flex justify-between text-left`}>
            <div className={tw`w-full flex-grow text-sm`}>
              <ResourceTitleColumn
                className={tw`w-full`}
                title={
                  <>
                    <span>{record.title}</span>
                    <div className={tw`flex gap-2 font-normal text-content-subtle text-xs`}>
                      <span className={tw`inline-block min-w-16 whitespace-nowrap`}>
                        RAF {record.rafScoreDisplay}
                      </span>
                      <span className={tw`inline-block min-w-16 whitespace-nowrap`}>
                        HCC {record.hccCode}
                      </span>
                      {record.confidenceRating && (
                        <ConfidenceLabel rating={record.confidenceRating} />
                      )}
                      {record.lastDiagnosedDisplay && (
                        <span
                          className={tw`w-1/2 space-x-1 whitespace-nowrap font-normal text-content-subtle text-xs`}
                        >
                          <FontAwesomeIcon icon={faCalendar} className="text-content-icon" />
                          <span>Last diagnosed {record.lastDiagnosedDisplay}</span>
                        </span>
                      )}
                    </div>
                  </>
                }
              />
            </div>
          </div>
        )}
      />
    );
  },
});
